import { images } from 'app/design/images';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { appStoreUrls } from 'app/lib/app-store';
import { getI18nLanguage } from 'app/lib/language';
import { safelyOpenURL } from 'app/lib/navigation';
import { env } from 'app/lib/env';
import React from 'react';
import { Pressable } from 'react-native';
import { useTranslation } from 'react-i18next';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const WEBSITE_ORIGIN = 'https://www.partage.club';
const CONTACT_EMAIL = 'info@partage.club';
const CONTACT_PHONE_DISPLAY = '438-476-9855';

interface FooterLinkProps {
  children: string;
  href?: string;
  underline?: boolean;
}

function FooterLink({ children, href, underline = true }: FooterLinkProps) {
  if (!href) {
    return (
      <Text color="text.primary" fontSize={16} lineHeight={20} textDecorationLine={underline ? 'underline' : 'none'}>
        {children}
      </Text>
    );
  }

  return (
    <Pressable
      accessibilityRole="link"
      onPress={() => void safelyOpenURL(href)}
      style={({ pressed }) => ({ opacity: pressed ? 0.6 : 1 })}
    >
      <Text color="text.primary" fontSize={16} lineHeight={20}>
        {children}
      </Text>
    </Pressable>
  );
}

function FooterColumn({ children, title }: { children: React.ReactNode; title: string }) {
  return (
    <Column flexBasis={160} flexGrow={1} gap="spacing.general.x6" minWidth={160} paddingRight="spacing.general.x12">
      <Text color="text.primary" fontSize={18} fontWeight="700" lineHeight={22}>
        {title}
      </Text>
      <Column gap="spacing.general.x3">{children}</Column>
    </Column>
  );
}

export function PartageClubFooter() {
  const { t } = useTranslation('web-footer');
  const language = getI18nLanguage();
  const locale = `${language}-ca`;
  const website = `${WEBSITE_ORIGIN}/${locale}`;
  const brandName = env.IS_PARTAKE ? 'Partake' : 'Partage Club';
  const contactEmail = env.IS_PARTAKE ? 'info@we-partake.com' : CONTACT_EMAIL;
  const appStoreImage = language === 'fr' ? images.guest_app_store_fr : images.app_store_en;
  const googlePlayImage = language === 'fr' ? images.guest_google_play_fr : images.google_play_en;

  return (
    <Column
      alignSelf="stretch"
      backgroundColor="surface.primary-neutral"
      marginTop="spacing.general.x8"
      style={{ width: '100%' }}
      testID="partage-club-footer"
    >
      <Column
        alignSelf="center"
        gap="spacing.general.x14"
        maxWidth={1920}
        paddingBottom="spacing.general.x16"
        paddingHorizontal="spacing.general.x6"
        paddingTop="spacing.general.x8"
        style={{ width: '100%' }}
      >
        <Row alignItems="flex-start" flexWrap="wrap" style={{ rowGap: theme.spacing['spacing.general.x10'] }}>
          <FooterColumn title={t('openingHours.title')}>
            <Text color="text.primary" fontSize={16} lineHeight={20}>
              {t('openingHours.weekdays')}
            </Text>
            <Text color="text.primary" fontSize={16} lineHeight={20}>
              {t('openingHours.hours')}
            </Text>
          </FooterColumn>

          <FooterColumn title={t('follow.title')}>
            <FooterLink href="https://facebook.com/partageclubapp">Facebook</FooterLink>
            <FooterLink href="https://www.instagram.com/partageclub">Instagram</FooterLink>
            <FooterLink href="https://linkedin.com/company/partageclubapp">LinkedIn</FooterLink>
          </FooterColumn>

          <FooterColumn title={t('about.title')}>
            <FooterLink href={`${WEBSITE_ORIGIN}/levee-de-fonds`}>{t('about.fundraising')}</FooterLink>
            <FooterLink href={`mailto:${contactEmail}?subject=${encodeURIComponent(t('about.pressKit'))}`}>
              {t('about.pressKit')}
            </FooterLink>
            <FooterLink href={`${website}/about/career`}>{t('about.joinTeam')}</FooterLink>
            <FooterLink href={`${website}/city-solution/why-partake`}>{t('about.myCity')}</FooterLink>
            <FooterLink href={`${website}/about/frequently-asked-questions`}>{t('about.faq')}</FooterLink>
            <FooterLink href={`${website}/terms-and-conditions-ca`}>{t('about.terms')}</FooterLink>
            <FooterLink href={`${website}/privacy-policy`}>{t('about.privacy')}</FooterLink>
          </FooterColumn>

          <FooterColumn title={t('community.title')}>
            <FooterLink href={`${website}/cadeau`}>{t('community.gift')}</FooterLink>
            <FooterLink href={`${website}/app/get-involved`}>{t('community.ambassador')}</FooterLink>
            <FooterLink href={`${website}/app/my-area`}>{t('community.neighbourhood')}</FooterLink>
          </FooterColumn>

          <Column flexBasis={210} flexGrow={0} flexShrink={0} minWidth={210}>
            <FooterColumn title={brandName}>
              <FooterLink href={`mailto:${contactEmail}`} underline={false}>
                {contactEmail}
              </FooterLink>
              <FooterLink href="tel:+14384769855">{CONTACT_PHONE_DISPLAY}</FooterLink>
            </FooterColumn>

            <Column gap="spacing.general.x3" marginTop="spacing.general.x4">
              <Text color="text.primary" fontSize={18} fontWeight="700" lineHeight={22}>
                {t('apps')}
              </Text>
              <Column gap="spacing.general.x2">
                <Pressable accessibilityRole="link" onPress={() => void safelyOpenURL(appStoreUrls.ios)}>
                  <PCImage contentFit="contain" height={48} source={appStoreImage} width={152} />
                </Pressable>
                <Pressable accessibilityRole="link" onPress={() => void safelyOpenURL(appStoreUrls.android)}>
                  <PCImage contentFit="contain" height={48} source={googlePlayImage} width={162} />
                </Pressable>
              </Column>
            </Column>
          </Column>
        </Row>

        {env.IS_PARTAKE ? (
          <PCImage
            style={{ bottom: theme.spacing['spacing.general.x12'] }}
            contentFit="fill"
            height={56}
            source={images.footer_partake_logo}
            width={283}
          />
        ) : (
          <PCImage
            style={{ bottom: theme.spacing['spacing.general.x12'] }}
            contentFit="fill"
            height={56}
            source={images.footer_partage_club_logo}
            width={283}
          />
        )}
      </Column>
    </Column>
  );
}
